import type { Questionnaire } from '@medplum/fhirtypes'
import {
  getComputationAnswerOptions,
  getComputationQuestionnaireItem,
  QUESTIONNAIRE_ITEM_DROPDOWN_EXTENSION,
  VALIDATED_TOOL_SUMMARY_ITEM,
  VALIDATED_TOOL_TAG,
} from '#src/models/questionnaires/utils/index.ts'
import {
  QUESTIONNAIRE_ITEM_DISPLAY_ACCORDION_URL,
  QUESTIONNAIRE_ITEM_DISPLAY_LINK_URL,
} from '#src/structureDefinitions.ts'

const ADL_ANSWER_OPTIONS = getComputationAnswerOptions([
  { code: 'independent', score: 1 },
  { code: 'dependent', score: 0 },
])

export const adlQuestionnaire = {
  resourceType: 'Questionnaire',
  url: 'https://www.seenhealth.org/fhir/Questionnaire/ADL',
  title: 'Katz Index of Independence in Activities of Daily Living (ADL)',
  name: 'ADL',
  status: 'active',
  subjectType: ['Patient'],
  meta: {
    tag: [VALIDATED_TOOL_TAG],
  },
  item: [
    {
      linkId: 'instructions',
      type: 'display',
      text: 'For determining level of independence for activities of daily living',
    },
    {
      linkId: QUESTIONNAIRE_ITEM_DISPLAY_LINK_URL,
      type: 'display',
      text: 'ADL PDF',
      extension: [
        {
          url: QUESTIONNAIRE_ITEM_DISPLAY_LINK_URL,
          valueString:
            'https://www.alz.org/careplanning/downloads/katz-adl.pdf',
        },
      ],
    },
    {
      linkId: 'questions',
      type: 'group',
      item: [
        {
          linkId: 'bathing',
          type: 'choice',
          text: 'Bathing',
          required: true,
          answerOption: ADL_ANSWER_OPTIONS,
          extension: [QUESTIONNAIRE_ITEM_DROPDOWN_EXTENSION],
        },
        {
          linkId: QUESTIONNAIRE_ITEM_DISPLAY_ACCORDION_URL,
          type: 'display',
          prefix: 'Bathing Description',
          extension: [
            {
              url: QUESTIONNAIRE_ITEM_DISPLAY_ACCORDION_URL,
              valueString: '{ "accordion": "true" }',
            },
          ],
          text: '1. Independent: Bathes self completely or needs help in bathing only a single part of the body such as the back, genital area or disabled extremity.\n\n2. Dependent: Needs help with bathing more than one part of the body, getting in or out of the tub or shower. Requires total bathing',
        },
        {
          linkId: 'dressing',
          type: 'choice',
          text: 'Dressing',
          required: true,
          answerOption: ADL_ANSWER_OPTIONS,
          extension: [QUESTIONNAIRE_ITEM_DROPDOWN_EXTENSION],
        },
        {
          linkId: QUESTIONNAIRE_ITEM_DISPLAY_ACCORDION_URL,
          type: 'display',
          prefix: 'Dressing Description',
          extension: [
            {
              url: QUESTIONNAIRE_ITEM_DISPLAY_ACCORDION_URL,
              valueString: '{ "accordion": "true" }',
            },
          ],
          text: '1. Independent: Gets clothes from closets and drawers and puts on clothes and outer garments complete with fasteners. May have help tying shoes.\n\n2. Dependent: Needs help with dressing self or needs to be completely dressed.',
        },
        {
          linkId: 'toileting',
          type: 'choice',
          text: 'Toileting',
          required: true,
          answerOption: ADL_ANSWER_OPTIONS,
          extension: [QUESTIONNAIRE_ITEM_DROPDOWN_EXTENSION],
        },
        {
          linkId: QUESTIONNAIRE_ITEM_DISPLAY_ACCORDION_URL,
          type: 'display',
          prefix: 'Toileting Description',
          extension: [
            {
              url: QUESTIONNAIRE_ITEM_DISPLAY_ACCORDION_URL,
              valueString: '{ "accordion": "true" }',
            },
          ],
          text: '1. Independent: Goes to toilet, gets on and off, arranges clothes, cleans genital area without help.\n\n2. Dependent: Needs help transferring to the toilet, cleaning self or uses bedpan or commode.',
        },
        {
          linkId: 'transferring',
          type: 'choice',
          text: 'Transferring',
          required: true,
          answerOption: ADL_ANSWER_OPTIONS,
          extension: [QUESTIONNAIRE_ITEM_DROPDOWN_EXTENSION],
        },
        {
          linkId: QUESTIONNAIRE_ITEM_DISPLAY_ACCORDION_URL,
          type: 'display',
          prefix: 'Transferring Description',
          extension: [
            {
              url: QUESTIONNAIRE_ITEM_DISPLAY_ACCORDION_URL,
              valueString: '{ "accordion": "true" }',
            },
          ],
          text: '1. Independent: Moves in and out of bed or chair unassisted. Mechanical transfer aids are acceptable.\n\n2. Dependent: Needs help in moving from bed to chair or requires a complete transfer.',
        },
        {
          linkId: 'continence',
          type: 'choice',
          text: 'Continence',
          required: true,
          answerOption: ADL_ANSWER_OPTIONS,
          extension: [QUESTIONNAIRE_ITEM_DROPDOWN_EXTENSION],
        },
        {
          linkId: QUESTIONNAIRE_ITEM_DISPLAY_ACCORDION_URL,
          type: 'display',
          prefix: 'Continence Description',
          extension: [
            {
              url: QUESTIONNAIRE_ITEM_DISPLAY_ACCORDION_URL,
              valueString: '{ "accordion": "true" }',
            },
          ],
          text: '1. Independent: Exercises complete self control over urination and defecation.\n\n2. Dependent: Is partially or totally incontinent of bowel or bladder.',
        },
        {
          linkId: 'feeding',
          type: 'choice',
          text: 'Feeding',
          required: true,
          answerOption: ADL_ANSWER_OPTIONS,
          extension: [QUESTIONNAIRE_ITEM_DROPDOWN_EXTENSION],
        },
        {
          linkId: QUESTIONNAIRE_ITEM_DISPLAY_ACCORDION_URL,
          type: 'display',
          prefix: 'Feeding Description',
          extension: [
            {
              url: QUESTIONNAIRE_ITEM_DISPLAY_ACCORDION_URL,
              valueString: '{ "accordion": "true" }',
            },
          ],
          text: '1. Independent: Gets food from plate into mouth without help. Preparation of food may be done by another person.\n\n2. Dependent: Needs partial or total help with feeding or requires parenteral feeding.',
        },
      ],
    },
    getComputationQuestionnaireItem(),
    VALIDATED_TOOL_SUMMARY_ITEM,
  ],
} satisfies Questionnaire
